import { Deserializer } from 'jsonapi-serializer';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { createPagination } from './utilities/paginationBuilder';
import { APIConnector } from './api/APIConnector';
import { EnrolmentRelationship, EnrolmentRelationshipWrapper } from '../models/EnrolmentRelationship';
import { ListQuery } from '../models/ListQuery';
import { genericError } from '../models/GenericError';
import { INTERNAL_SERVER_ERROR } from '../constants/response-status-codes';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { isSuccessStatus } from "../utilities/requestResponseValidator";

const apiConnector = new APIConnector();

export const getEnrolmentRelationships = async (listParameters?: ListQuery): Promise<EnrolmentRelationshipWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=asset,master";
        const masterAssetFields = "fields[masterAsset]=masterAssetStart,asset,master";
        const masterFields = "fields[master]=mstDisplayname,mstIdnumber";
        const url = `masterAsset?${includes}&${masterAssetFields}&${masterFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject(err);
                }

                const enrolmentRelationships: EnrolmentRelationship[] = data.map((enrolmentRelationship): EnrolmentRelationship => {
                    return {
                        id: enrolmentRelationship['id'],
                        name: enrolmentRelationship['asset']['mst-displayname'],
                        dateLinked: getMasterAssetFormattedDatetime(enrolmentRelationship['master-asset-start']),
                        masterByMasterAssetId: enrolmentRelationship['asset']['id'],
                        masterByMasterId: enrolmentRelationship['master']['id'],
                        masterByMasterAssetUniqueIdentifier: enrolmentRelationship['asset']['mst-idnumber']
                    }
                });

                return resolve({ rows: enrolmentRelationships, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * Find a specific (should only ever be 1 or none) MasterGroup with masterId and masterByMasterAssetId matching.
 * We need this in order to delete the link (relationship to master).
 * @param masterId
 * @param masterByMasterAssetId
 */
export const getRelationship = async (masterId, masterByMasterAssetId): Promise<EnrolmentRelationship[]> => {
    try {
        const includes = "include=asset,master";
        const masterAssetFields = "fields[masterAsset]=masterAssetStart,asset,master";
        const masterFields = "fields[master]=mstDisplayname,mstIdnumber";
        const filter = `filter=master.id==${masterId};asset.id==${masterByMasterAssetId};maHistorical==0`;
        const url = `masterAsset?${includes}&${masterAssetFields}&${masterFields}&${filter}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, results: any[]) => {
                if (err) {
                    return reject(err);
                }
                const masterGroups: EnrolmentRelationship[] = results.map((enrolmentRelationship): EnrolmentRelationship => {
                    return {
                        id: enrolmentRelationship['id'],
                        name: enrolmentRelationship['asset']['mst-displayname'],
                        dateLinked: getMasterAssetFormattedDatetime(enrolmentRelationship['master-asset-start']),
                        masterByMasterAssetId: enrolmentRelationship['asset']['id'],
                        masterByMasterId: enrolmentRelationship['master']['id'],
                        masterByMasterAssetUniqueIdentifier: enrolmentRelationship['asset']['mst-idnumber']
                    };
                });

                return resolve(masterGroups);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const assignUnassignRelationship = async (toBeAssigned: string[], toBeUnassigned: string[], masterId: string) => {
    const newMasterGroups = buildToBeSavedRelationship(toBeAssigned, masterId);

    const addedMasterGroups = newMasterGroups.map(async (newMasterGroup) => {
        return await saveRelationship(newMasterGroup, true)
    })

    const removedAccessGroup = toBeUnassigned.map(async (accessGroupId) => {
        return await removeRelationship(masterId, accessGroupId)
    })

    return Promise.allSettled([...addedMasterGroups, ...removedAccessGroup]);
}

const buildToBeSavedRelationship = (toBeAssigned: string[], masterId: string) => {
    return toBeAssigned.map((masterByMasterAssetId) => {
        return ({
            data: {
                type: 'masterAsset',
                id: "",
                attributes: {
                    masterAssetExpiry: null,
                    maHistorical: 0,
                    reportableAssetStatus: 0,
                    masterAssetStart: generateMasterAssetDatetime()
                },
                relationships: {
                    master: {
                        data: {
                            type: 'master',
                            id: masterId
                        }
                    },
                    asset: {
                        data: {
                            type: 'master',
                            id: masterByMasterAssetId
                        }
                    }
                }
            }
        })
    })
}

const buildToBeRemovedRelationships = (enrolmentRelationship: EnrolmentRelationship, masterId) => {
    return {
        data: {
            type: 'masterAsset',
            id: enrolmentRelationship.id,
            attributes: {
                masterAssetExpiry: generateMasterAssetDatetime(),
                maHistorical: 1,
                reportableAssetStatus: 0,
            },
            relationships: {
                master: {
                    data: {
                        type: 'master',
                        id: masterId
                    }
                },
                asset: {
                    data: {
                        type: 'master',
                        id: enrolmentRelationship.masterByMasterAssetId
                    }
                }
            }
        }
    }
}


/**
 * POST a new Relationship which represents the relationship assigned to the master.
 * @param masterId
 * @param masterByMasterAssetId
 */
export const saveRelationship = async (relationship, isNewRelationship = true) => {
    try {
        const url = `masterAsset`;
        let response: Response;

        await apiConnector.sendData(url, isNewRelationship, relationship).then(async (data) => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve(true);
            }
            return reject({ status: response.status, message: genericError });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 *
 * Removes the relationship assignment from the person.
 * @param masterId
 * @param masterByMasterAssetId
 */
export const removeRelationship = async (masterId: string, masterByMasterAssetId): Promise<boolean> => {
    try {
        // First lookup the Master Asset
        const relationship = await getRelationship(masterId, masterByMasterAssetId);

        // Found the Master Asset, let's delete it then.
        if (relationship && relationship.length > 0) {
            const relationshipId = relationship[0].id;
            const url = `masterAsset/` + relationshipId;
            let response: Response;

            await apiConnector.sendData(url, false, buildToBeRemovedRelationships(relationship[0], masterId)).then(async (data) => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

            return new Promise(async (resolve, reject) => {
                if (response && isSuccessStatus(response.status)) {
                    return resolve(true);
                }
                return reject({ status: response.status, message: genericError });
            });
        }
        // Unable to delete the Master Asset. Couldn't find it.
        return new Promise<boolean>(async (resolve, reject) => {
            reject({ status: INTERNAL_SERVER_ERROR, message: genericError });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const generateMasterAssetDatetime = (): string => {
    const datetime = new Date();
    const day = datetime.getDate() < 10 ? `0${datetime.getDate()}` : datetime.getDate();
    const month = datetime.getMonth() < 10 ? `0${datetime.getMonth() + 1}` : datetime.getMonth() + 1;
    const year = datetime.getFullYear();
    const hours = datetime.getHours() < 10 ? `0${datetime.getHours()}` : datetime.getHours();
    const minutes = datetime.getMinutes() < 10 ? `0${datetime.getMinutes()}` : datetime.getMinutes();
    const seconds = datetime.getSeconds() < 10 ? `0${datetime.getSeconds()}` : datetime.getSeconds();
    const milliseconds = datetime.getMilliseconds() ? `0${datetime.getMilliseconds()}` : datetime.getMilliseconds();

    return `${day}-${month}-${year}T${hours}:${minutes}:${seconds}.${milliseconds}`
}

const getMasterAssetFormattedDatetime = (datetime: string): string => {
    const date = datetime.split('T')[0]
    const time = datetime.split('T')[1].substring(0, datetime.split('T')[1].indexOf('.'))

    return date + ' ' + time;
}
